import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Footer from './Footer';
import AuthModal from './AuthModal';
import './NeuralHomepage.css';

const HERO_IMG = '/images/home/hero-career-copilot.png';

/* ---------- Three.js neural-orb canvas ---------- */
const HeroNeuralCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 80;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(224,163,255,0.95)');
      g.addColorStop(0.7, 'rgba(255,105,180,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    const RINGS = [
      { count: 18, radius: 22, speed: 0.20, tilt: 0.0 },
      { count: 26, radius: 36, speed: -0.14, tilt: 0.5 },
      { count: 34, radius: 50, speed: 0.10, tilt: -0.4 },
    ];
    const ringObjs = RINGS.map((spec) => {
      const positions = new Float32Array(spec.count * 3);
      const angles = new Float32Array(spec.count);
      for (let i = 0; i < spec.count; i++) angles[i] = (i / spec.count) * Math.PI * 2;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        size: 2.2, map: sprite, transparent: true,
        depthWrite: false, blending: THREE.AdditiveBlending, color: 0xffffff,
      });
      const pts = new THREE.Points(geo, mat);
      scene.add(pts);
      return { spec, geo, mat, angles, pts };
    });

    const fieldCount = 200;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 200;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 120;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 70;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.10;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 0.8, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.40,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    const coreGeo = new THREE.IcosahedronGeometry(8, 1);
    const core = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({
      color: 0xe0a3ff, wireframe: true, transparent: true, opacity: 0.40,
    }));
    scene.add(core);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.01;
      ringObjs.forEach(({ spec, geo, angles }) => {
        const pos = geo.attributes.position.array;
        for (let i = 0; i < spec.count; i++) {
          const a = angles[i] + t * spec.speed;
          pos[i * 3]     = Math.cos(a) * spec.radius;
          pos[i * 3 + 1] = Math.sin(a) * spec.radius * Math.cos(spec.tilt);
          pos[i * 3 + 2] = Math.sin(a) * spec.radius * Math.sin(spec.tilt);
        }
        geo.attributes.position.needsUpdate = true;
      });
      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 60) fp[i * 3 + 1] = -60;
      }
      fieldGeo.attributes.position.needsUpdate = true;
      core.rotation.x += 0.005; core.rotation.y += 0.006;
      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      ringObjs.forEach(({ geo, mat }) => { geo.dispose(); mat.dispose(); });
      fieldGeo.dispose(); fieldMat.dispose();
      coreGeo.dispose(); core.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);
  return <div ref={mountRef} className="nh-hero-canvas" aria-hidden="true" />;
};

/* ---------- Floating product tiles around the hero ---------- */
const FLOATING_PRODUCTS = [
  // left column (top → bottom) — kept clear of the floating header at the top
  { side: 'left', y: '26%',  icon: '🏆', name: 'Interview Assistant', path: '/InterviewAssistant' },
  { side: 'left', y: '42%',  icon: '🔮', name: 'Mock Interview',      path: '/mockInterview' },
  { side: 'left', y: '58%',  icon: '📄', name: 'Resume Builder',      path: '/resume-builder' },
  { side: 'left', y: '74%',  icon: '🛡️', name: 'Cover Letter',        path: '/CoverLetter' },
  { side: 'left', y: '90%',  icon: '⭐', name: 'ATS Check',           path: '/ATSCheck' },
  // right column
  { side: 'right', y: '26%', icon: '💎', name: 'Interview Questions', path: '/InterviewQuestions' },
  { side: 'right', y: '42%', icon: '📈', name: 'LinkedIn Optimizer',  path: '/LinkedInOptimizer' },
  { side: 'right', y: '58%', icon: '⚡', name: 'Auto Apply',           path: '/AutoJobApply' },
  { side: 'right', y: '74%', icon: '🌐', name: 'Portfolio Builder',   path: '/portfolio' },
  { side: 'right', y: '90%', icon: '🎓', name: 'Course Creator',      path: '/certification' },
];

const FloatingProducts = ({ onNav }) => (
  <div className="nh-floats" aria-hidden="false">
    {FLOATING_PRODUCTS.map((p, i) => (
      <button
        key={p.name}
        type="button"
        onClick={() => onNav(p.path)}
        className={`nh-float nh-float-${p.side}`}
        style={{ top: p.y, '--nh-i': i }}
      >
        <span className="nh-float-icon" aria-hidden="true">{p.icon}</span>
        <span className="nh-float-name">{p.name}</span>
      </button>
    ))}
  </div>
);

/* ---------- Hero ---------- */
const Hero = ({ onCTA, onNav }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section className="nh-hero" ref={ref}>
      <HeroNeuralCanvas />
      <div className="nh-hero-bg" />
      <div className="nh-hero-halo" aria-hidden="true" />
      <img src={HERO_IMG} alt="" className="nh-hero-img" loading="eager" />

      <FloatingProducts onNav={onNav} />

      <div className="nh-hero-content">
        <span className="nh-hero-pill">
          <span className="nh-pill-dot" /> #1 AI Career Copilot
        </span>
        <h1 className="nh-h1">
          Ace Your <span className="nh-grad-text">Dream Job</span>.
        </h1>
        <p className="nh-hero-sub">
          TERVIEW listens to your interview in real time and feeds you exactly what to say —
          structured answers, code suggestions, coaching. Walk in nervous. Walk out hired.
        </p>
        <div className="nh-hero-cta">
          <button className="nh-btn nh-btn-primary" onClick={onCTA}>
            Start free
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
          <a className="nh-btn nh-btn-ghost" href="#features">Explore features</a>
        </div>

        <div className="nh-hero-proof">
          <div className="nh-proof-item">
            <div className="nh-proof-stars">★★★★★</div>
            <div className="nh-proof-meta">4.8 · 2,739 reviews</div>
          </div>
          <div className="nh-proof-divider" />
          <div className="nh-proof-item">
            <div className="nh-proof-num">1M+</div>
            <div className="nh-proof-meta">Users</div>
          </div>
          <div className="nh-proof-divider" />
          <div className="nh-proof-item">
            <div className="nh-proof-num">116ms</div>
            <div className="nh-proof-meta">Avg latency</div>
          </div>
          <div className="nh-proof-divider" />
          <div className="nh-proof-item">
            <div className="nh-proof-num">50+</div>
            <div className="nh-proof-meta">Languages</div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Trusted-by logos ---------- */
const TRUSTED_LOGOS = [
  { name: 'Google',    src: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' },
  { name: 'Amazon',    src: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' },
  { name: 'Microsoft', src: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg' },
  { name: 'Apple',     src: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg' },
  { name: 'Netflix',   src: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg' },
  { name: 'Meta',      src: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg' },
  { name: 'LinkedIn',  src: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Linkedin.svg' },
  { name: 'Stripe',    src: 'https://upload.wikimedia.org/wikipedia/commons/b/b0/Stripe_logo%2C_revised_2016.svg' },
];
const TrustedBy = () => (
  <section className="nh-trusted">
    <p className="nh-trusted-title">Trusted by professionals at</p>
    <div className="nh-trusted-track-wrap">
      <div className="nh-trusted-track">
        {[...TRUSTED_LOGOS, ...TRUSTED_LOGOS].map((l, i) => (
          <div key={i} className="nh-trusted-logo">
            <img src={l.src} alt={l.name} loading="lazy" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Core Features ---------- */
const CORE_FEATURES = [
  { title: 'Interview Copilot',  body: 'Real-time transcription + STAR-formatted answers fed to you as the question lands.', tag: 'Live',     path: '/InterviewAssistant', icon: '🏆' },
  { title: 'Mock Interview',      body: '15 personalized questions, full model answers, AI-graded delivery on 5 axes.',          tag: 'Practice', path: '/mockInterview',      icon: '🔮' },
  { title: 'Resume Builder',      body: 'AI-rewritten bullets, ATS-optimized templates, downloadable in seconds.',                tag: 'ATS',      path: '/resume-builder',     icon: '📄' },
  { title: 'Cover Letter',        body: 'Company-specific opener, quantified hook from your resume, exported in 90 seconds.',     tag: 'Compose',  path: '/CoverLetter',        icon: '🛡️' },
  { title: 'ATS Check',           body: 'Single ATS Match score + per-keyword breakdown vs. the JD. One-click tailoring.',         tag: 'Score',    path: '/ATSCheck',           icon: '⭐' },
  { title: 'Interview Questions', body: '50 personalized questions with full STAR-structured model answers — practice anywhere.',   tag: 'Drill',    path: '/InterviewQuestions', icon: '💎' },
  { title: 'LinkedIn Optimizer',  body: 'AI-rewritten headline, summary, experience — tuned to recruiter searches. 5× more views.', tag: 'Visibility', path: '/LinkedInOptimizer',  icon: '📈' },
  { title: 'Auto Apply',          body: 'Real browser agent applies to LinkedIn, Indeed, Dice, Glassdoor on your behalf.',         tag: 'Agent',    path: '/AutoJobApply',       icon: '⚡' },
  { title: 'Portfolio Builder',   body: 'Five hand-tuned templates, live WYSIWYG preview, sharable /p/handle link.',                tag: 'Showcase', path: '/portfolio',          icon: '🌐' },
  { title: 'Course Creator',      body: 'AI-generated learning roadmap, day-by-day modules, quiz-gated mastery, verified cert.',    tag: 'Learn',    path: '/certification',      icon: '🎓' },
];
const CoreFeatures = ({ onNav }) => (
  <section className="nh-section" id="features">
    <div className="nh-section-head">
      <span className="nh-eyebrow">CORE FEATURES</span>
      <h2 className="nh-h2">Everything you need <span className="nh-grad-text">in the interview room</span>.</h2>
      <p className="nh-lead nh-center">
        Four flagship tools, one ecosystem. Each works independently — together, they're an unfair advantage.
      </p>
    </div>
    <div className="nh-feature-grid">
      {CORE_FEATURES.map((f) => (
        <button key={f.title} type="button" className="nh-feature-card" onClick={() => onNav(f.path)}>
          <div className="nh-feature-icon" aria-hidden="true">{f.icon}</div>
          <span className="nh-feature-tag">{f.tag}</span>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
          <span className="nh-feature-arrow">→</span>
        </button>
      ))}
    </div>
  </section>
);

/* ---------- Setup steps ---------- */
const SETUP_STEPS = [
  { n: '01', title: 'Create your account', body: 'Sign up free in 30 seconds. No card required.' },
  { n: '02', title: 'Upload your resume', body: 'Or paste your profile. The AI grounds every answer in your real story.' },
  { n: '03', title: 'Practice or go live', body: 'Run mock interviews to drill, or share your live interview tab to get real-time help.' },
  { n: '04', title: 'Review & improve', body: 'Per-answer scoring, transcript playback, and gap analysis after every session.' },
];
const SetupSteps = ({ onCTA }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= SETUP_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="nh-section" ref={ref}>
      <div className="nh-section-head">
        <span className="nh-eyebrow">SIMPLE SETUP</span>
        <h2 className="nh-h2">From signup to interview-ready in <span className="nh-grad-text">90 seconds</span>.</h2>
      </div>
      <div className="nh-steps">
        {SETUP_STEPS.map((s, i) => (
          <div key={s.n} className={`nh-step ${i <= active ? 'lit' : ''}`}>
            <div className="nh-step-num">{s.n}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < SETUP_STEPS.length - 1 && <div className={`nh-step-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
      <div className="nh-section-cta">
        <button className="nh-btn nh-btn-primary" onClick={onCTA}>Get started free</button>
      </div>
    </section>
  );
};

/* ---------- Desktop App section ---------- */
const DESKTOP_FEATURES = [
  { n: '01', title: 'Stealth mode', body: 'The assistant runs invisible to screen-share and recording software. Only you see it.' },
  { n: '02', title: 'Always-on overlay', body: 'Keep the panel above any meeting window. No tab-switching mid-question.' },
  { n: '03', title: 'Mouse-free shortcuts', body: 'Toggle, accept, regenerate — all from the keyboard. Eyes stay on the camera.' },
  { n: '04', title: 'IDE integrations', body: 'VSCode + Cursor extensions for live coding rounds. The hint sits inline.' },
];
const DesktopApp = () => (
  <section className="nh-section nh-desktop">
    <div className="nh-section-head">
      <span className="nh-eyebrow">DESKTOP APP</span>
      <h2 className="nh-h2">A native app built for <span className="nh-grad-text">live interviews</span>.</h2>
      <p className="nh-lead nh-center">
        Mac and Windows. Stealth from the meeting, instant from the keyboard.
      </p>
    </div>
    <div className="nh-desktop-grid">
      {DESKTOP_FEATURES.map((f) => (
        <div key={f.n} className="nh-desktop-card">
          <div className="nh-desktop-num">{f.n}</div>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
        </div>
      ))}
    </div>
    <div className="nh-section-cta nh-desktop-cta">
      <a className="nh-btn nh-btn-primary" href="/downloads/TERVIEW-1.0.0-mac.dmg">
         Mac (.dmg)
      </a>
      <a className="nh-btn nh-btn-ghost" href="/downloads/TERVIEW Setup 1.0.0.exe">
         Windows (.exe)
      </a>
    </div>
  </section>
);

/* ---------- Why TERVIEW ---------- */
const WHY_CARDS = [
  { title: 'Sub-second latency', body: 'Suggestions render before you finish processing the question.', icon: '⚡' },
  { title: 'Multilingual', body: '50+ languages. Localized phrasing, idiom-aware.', icon: '🌐' },
  { title: 'All major LLMs', body: 'GPT-5, Claude Sonnet 4.6, Gemini, Llama — pick your model.', icon: '🧠' },
  { title: 'Career OS, not just an app', body: 'Resume, ATS, cover letter, portfolio, mock — one connected suite.', icon: '🛰️' },
  { title: 'Practice anywhere', body: 'Web, desktop, IDE. Sessions sync across all of them.', icon: '🪐' },
  { title: 'Built by interviewers', body: 'Every feature shipped by people who hired and got hired here.', icon: '🛠️' },
];
const WhyTerview = () => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">WHY TERVIEW</span>
      <h2 className="nh-h2">The most <span className="nh-grad-text">advanced</span> interview platform.</h2>
      <p className="nh-lead nh-center">
        Backed by every cutting-edge model, built for every interview format, designed for every candidate.
      </p>
    </div>
    <div className="nh-why-grid">
      {WHY_CARDS.map((c) => (
        <div key={c.title} className="nh-why-card">
          <div className="nh-why-icon">{c.icon}</div>
          <h3>{c.title}</h3>
          <p>{c.body}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Scenarios ---------- */
const SCENARIOS = [
  { title: 'System Design', body: 'Sketch architectures live with the AI suggesting components, tradeoffs, and edge cases.' },
  { title: 'Behavioral / STAR', body: 'Story scaffolds drawn from your resume, structured Situation→Task→Action→Result.' },
  { title: 'Software Engineering', body: 'DSA hints, complexity analysis, and idiomatic-language suggestions for your stack.' },
  { title: 'Product Manager', body: 'Frameworks (CIRCLES, RICE, AARRR) suggested per question, customized to the role.' },
  { title: 'Sales / Customer Success', body: 'Discovery prompts, objection-handling, and competitor positioning live.' },
  { title: 'Consulting Cases', body: 'MECE breakdowns, hypothesis trees, and number sense support during case interviews.' },
];
const Scenarios = () => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">SCENARIOS</span>
      <h2 className="nh-h2">Supports <span className="nh-grad-text">every interview format</span>.</h2>
      <p className="nh-lead nh-center">
        From algorithm rounds to executive coffee chats. The assistant adapts to your exact format and industry.
      </p>
    </div>
    <div className="nh-scenarios-grid">
      {SCENARIOS.map((s) => (
        <div key={s.title} className="nh-scenario">
          <h3>{s.title}</h3>
          <p>{s.body}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Integrations ---------- */
const INTEGRATIONS = ['Zoom', 'Google Meet', 'Microsoft Teams', 'Webex', 'BlueJeans', 'HireVue', 'CodeSignal', 'HackerRank'];
const Integrations = () => (
  <section className="nh-section nh-integrations">
    <div className="nh-section-head">
      <span className="nh-eyebrow">INTEGRATIONS</span>
      <h2 className="nh-h2">Works with <span className="nh-grad-text">every meeting platform</span>.</h2>
      <p className="nh-lead nh-center">
        We capture system audio, so anything you can share, we can support.
      </p>
    </div>
    <div className="nh-integrations-grid">
      {INTEGRATIONS.map((p) => (
        <div key={p} className="nh-integration">{p}</div>
      ))}
    </div>
  </section>
);

/* ---------- Testimonials ---------- */
const TESTIMONIALS = [
  { name: 'Sarah Mitchell',   role: 'SWE @ Google',   text: 'I walked into the Google final feeling like I had been there before. The Copilot turned every question into a STAR I already had a draft of.' },
  { name: 'Michael Chen',     role: 'PM @ Amazon',    text: 'The mock interview drilled me on 15 questions in an hour. The grading was sharper than my actual hiring committee.' },
  { name: 'Emily Rodriguez',  role: 'DS @ Microsoft', text: 'For coding rounds, the inline hints in VSCode never gave away the answer — they kept me unblocked. Exactly the right level.' },
  { name: 'David Thompson',   role: 'UX @ Apple',     text: 'I went from "I never get past phone screens" to four onsites in two months. The resume tailoring is what unlocked it.' },
];
const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="nh-section">
      <div className="nh-section-head">
        <span className="nh-eyebrow">TESTIMONIALS</span>
        <h2 className="nh-h2">Real results from <span className="nh-grad-text">real candidates</span>.</h2>
      </div>
      <div className="nh-testimonial-card">
        <div className="nh-testimonial-stars">★★★★★</div>
        <p className="nh-testimonial-text">"{TESTIMONIALS[idx].text}"</p>
        <div className="nh-testimonial-meta">
          <span className="nh-testimonial-name">{TESTIMONIALS[idx].name}</span>
          <span className="nh-testimonial-role">{TESTIMONIALS[idx].role}</span>
        </div>
        <div className="nh-testimonial-dots">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`nh-test-dot ${i === idx ? 'active' : ''}`}
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Comparison table ---------- */
const COMPARE_ROWS = [
  { feature: 'Real-time answers in interviews', terview: true,  generic: false, traditional: false },
  { feature: 'STAR-formatted from your resume',  terview: true,  generic: false, traditional: false },
  { feature: 'Coding round hints (live)',         terview: true,  generic: false, traditional: false },
  { feature: 'AI-graded mock interviews',         terview: true,  generic: false, traditional: 'limited' },
  { feature: '50+ languages',                     terview: true,  generic: 'limited', traditional: false },
  { feature: 'Auto-applies to job boards',        terview: true,  generic: false, traditional: false },
  { feature: 'Resume / ATS / cover letter suite', terview: true,  generic: false, traditional: false },
  { feature: 'Free plan',                          terview: true,  generic: 'limited', traditional: false },
];
const ComparisonCell = ({ value }) => {
  if (value === true)        return <span className="nh-cell yes">✓</span>;
  if (value === false)       return <span className="nh-cell no">—</span>;
  if (value === 'limited')   return <span className="nh-cell mid">partial</span>;
  return <span className="nh-cell">{value}</span>;
};
const Comparison = () => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">COMPARISON</span>
      <h2 className="nh-h2">Not all AI tools are <span className="nh-grad-text">the same</span>.</h2>
      <p className="nh-lead nh-center">
        How TERVIEW stacks up against generic AI chatbots and traditional interview prep.
      </p>
    </div>
    <div className="nh-compare-card">
      <div className="nh-compare-row nh-compare-head">
        <div className="nh-compare-feat" />
        <div className="nh-compare-col nh-compare-col-us">TERVIEW</div>
        <div className="nh-compare-col">Generic AI tools</div>
        <div className="nh-compare-col">Traditional prep</div>
      </div>
      {COMPARE_ROWS.map((r) => (
        <div key={r.feature} className="nh-compare-row">
          <div className="nh-compare-feat">{r.feature}</div>
          <div className="nh-compare-col nh-compare-col-us"><ComparisonCell value={r.terview} /></div>
          <div className="nh-compare-col"><ComparisonCell value={r.generic} /></div>
          <div className="nh-compare-col"><ComparisonCell value={r.traditional} /></div>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Pricing teaser ---------- */
const Pricing = ({ onCTA }) => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">PRICING</span>
      <h2 className="nh-h2">Is TERVIEW free? <span className="nh-grad-text">Yes.</span></h2>
      <p className="nh-lead nh-center">
        Core tools are free forever. Upgrade for unlimited live sessions, premium models, and the desktop app.
      </p>
    </div>
    <div className="nh-pricing-grid">
      <div className="nh-price-card">
        <div className="nh-price-head">Free</div>
        <div className="nh-price-num">$0</div>
        <div className="nh-price-sub">forever · for everyone</div>
        <ul className="nh-price-list">
          <li>5 mock interviews / month</li>
          <li>Resume Builder + 4 templates</li>
          <li>ATS Check unlimited</li>
          <li>Cover Letter generator</li>
          <li>Portfolio Builder</li>
        </ul>
        <button className="nh-btn nh-btn-ghost" onClick={onCTA}>Start free</button>
      </div>
      <div className="nh-price-card nh-price-card-featured">
        <div className="nh-price-badge">Most popular</div>
        <div className="nh-price-head">Pro</div>
        <div className="nh-price-num">$29<span>/mo</span></div>
        <div className="nh-price-sub">monthly · cancel anytime</div>
        <ul className="nh-price-list">
          <li>Unlimited live Interview Copilot</li>
          <li>Unlimited mock interviews</li>
          <li>Desktop stealth app (Mac + Win)</li>
          <li>VSCode + Cursor IDE integration</li>
          <li>Premium LLMs (GPT-5, Sonnet 4.6)</li>
          <li>Auto-Apply agent (50 jobs/day)</li>
        </ul>
        <button className="nh-btn nh-btn-primary" onClick={onCTA}>Start Pro free for 7 days</button>
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Will the interviewer see the assistant?', a: 'No. The assistant runs inside your tab or desktop app — never on the meeting itself. Stealth mode hides it from screen-share APIs.' },
  { q: 'How fast are the suggestions?', a: 'Average end-to-end latency is 116ms — usually faster than your own thought formation. By the time you start answering, you already have the structure.' },
  { q: 'Does it work for coding rounds?', a: 'Yes. Use our VSCode/Cursor extensions for live inline hints. We surface algorithm direction and complexity, never the literal solution.' },
  { q: 'What languages are supported?', a: '50+ spoken languages with native-speaker phrasing, plus all major programming languages.' },
  { q: 'Is my interview audio stored?', a: 'No. Audio is transcribed in-stream and discarded the moment the session ends, unless you explicitly save the transcript.' },
  { q: 'How is this different from ChatGPT?', a: 'ChatGPT requires you to paste questions and read outputs. TERVIEW listens, knows your resume + the JD, and surfaces a fully-structured answer in real time without you typing a thing.' },
];
const FAQAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="nh-section">
      <div className="nh-section-head">
        <span className="nh-eyebrow">SUPPORT</span>
        <h2 className="nh-h2">Frequently asked <span className="nh-grad-text">questions</span></h2>
      </div>
      <div className="nh-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`nh-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="nh-faq-q">
              <span>{f.q}</span>
              <span className="nh-faq-chev">+</span>
            </div>
            <div className="nh-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Final CTA ---------- */
const FinalCTA = ({ onCTA }) => (
  <section className="nh-cta-final">
    <div className="nh-cta-card">
      <div className="nh-cta-glow" />
      <h2 className="nh-h2">
        Your next interview could be <span className="nh-grad-text">the one</span>.
      </h2>
      <p>Join 1M+ professionals who landed their dream job with a co-pilot in their ear. Start in 90 seconds.</p>
      <div className="nh-hero-cta center">
        <button className="nh-btn nh-btn-primary" onClick={onCTA}>
          Start free
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
        </button>
      </div>
    </div>
  </section>
);

/* ---------- Page root ---------- */
const NeuralHomepage = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Auto-open the modal if any landing page redirected here with ?openLogin=1.
  // Strip the query so reload doesn't re-trigger.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('openLogin') === '1') {
      setShowAuthModal(true);
      params.delete('openLogin');
      const cleaned = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', cleaned);
    }
  }, []);

  // Always navigate directly. Destination pages own their auth/credit gating.
  const onCTA = useCallback(() => navigate('/InterviewAssistant'), [navigate]);
  const onNav = useCallback((path) => navigate(path), [navigate]);

  return (
    <div className="nh-page">
      <Header setShowAuthModal={setShowAuthModal} />

      <Hero onCTA={onCTA} onNav={onNav} />
      <TrustedBy />
      <CoreFeatures onNav={onNav} />
      <SetupSteps onCTA={onCTA} />
      <DesktopApp />
      <WhyTerview />
      <Scenarios />
      <Integrations />
      <Testimonials />
      <Comparison />
      <Pricing onCTA={onCTA} />
      <FAQAccordion />
      <FinalCTA onCTA={onCTA} />

      <Footer />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default NeuralHomepage;
